'use client';

import React, { memo, useMemo } from 'react';
import {
  useFormContext,
  Controller,
  ControllerRenderProps,
  ControllerFieldState,
  FieldValues,
  FieldPath,
  UseFormStateReturn,
  Control,
} from 'react-hook-form';

import { Text } from '../text';
import { VStack } from '../stack';
import { memoizeComponent, shallowPropsAreEqualIgnoreFunctions } from '../../utils/performance';
import { useOptimizedCallback } from '../../hooks/use-performance';

export interface OptimizedFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  /** Field name in the form */
  name: TName;
  /** Field label */
  label?: string;
  /** Helper text */
  helperText?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Render prop for the input component */
  render: (props: {
    field: ControllerRenderProps;
    error?: string;
    isDirty: boolean;
    isTouched: boolean;
  }) => React.ReactElement;
  /** Skip re-render on form state changes */
  skipFormStateUpdate?: boolean;
  /** Custom validation */
  rules?: Record<string, unknown>;
}

/**
 * Internal field component that handles the actual rendering
 */
const FieldContent = memo<{
  label?: string;
  helperText?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}>(({ label, helperText, required, error, children }) => {
  return (
    <VStack align="stretch" gap={2}>
      {label && (
        <Text as="label" fontWeight="medium">
          {label}
          {required && (
            <Text as="span" color="fg.error" marginLeft={1}>
              *
            </Text>
          )}
        </Text>
      )}

      {children}

      {error ? (
        <Text fontSize="sm" color="fg.error" role="alert">
          {error}
        </Text>
      ) : helperText ? (
        <Text fontSize="sm" color="fg.muted">
          {helperText}
        </Text>
      ) : null}
    </VStack>
  );
}, shallowPropsAreEqualIgnoreFunctions);

FieldContent.displayName = 'FieldContent';

/**
 * Optimized form field component that minimizes re-renders
 * Uses React Hook Form's Controller with performance optimizations
 */
export const OptimizedFormFieldComponent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  helperText,
  required = false,
  disabled = false,
  render,
  skipFormStateUpdate = false,
  rules = {},
}: OptimizedFormFieldProps<TFieldValues, TName>) => {
  const { control, formState } = useFormContext();

  // Memoize rules to prevent Controller re-renders
  const memoizedRules = useMemo(
    () => ({
      ...rules,
      required: required ? 'This field is required' : false,
    }),
    [rules, required]
  );

  // Get error only if not skipping form state updates
  const error = !skipFormStateUpdate ? formState.errors[name]?.message : undefined;

  // Memoize the render callback
  const renderField = useOptimizedCallback(
    (renderProps: unknown) => {
      const {
        field,
        fieldState,
        formState: _formState,
      } = renderProps as {
        field: ControllerRenderProps<TFieldValues, TName>;
        fieldState: ControllerFieldState;
        formState?: UseFormStateReturn<TFieldValues>;
      };
      const isDirty = !skipFormStateUpdate && fieldState.isDirty;
      const isTouched = !skipFormStateUpdate && fieldState.isTouched;

      return (
        <FieldContent
          label={label}
          helperText={helperText}
          required={required}
          error={error as string}
        >
          {render({
            field: {
              ...field,
              disabled: disabled || field.disabled,
            },
            error: error as string,
            isDirty,
            isTouched,
          })}
        </FieldContent>
      );
    },
    [label, helperText, required, disabled, error, render, skipFormStateUpdate]
  );

  return (
    <Controller
      name={name}
      control={control as Control<FieldValues>}
      rules={memoizedRules}
      render={
        renderField as ({
          field,
          fieldState,
          formState,
        }: {
          field: ControllerRenderProps<FieldValues, typeof name>;
          fieldState: ControllerFieldState;
          formState: UseFormStateReturn<FieldValues>;
        }) => React.ReactElement
      }
    />
  );
};

// Export memoized version
export const OptimizedFormField = memoizeComponent(
  OptimizedFormFieldComponent,
  (prevProps, nextProps) => {
    // Custom comparison that ignores render function changes
    return (
      prevProps.name === nextProps.name &&
      prevProps.label === nextProps.label &&
      prevProps.helperText === nextProps.helperText &&
      prevProps.required === nextProps.required &&
      prevProps.disabled === nextProps.disabled &&
      prevProps.skipFormStateUpdate === nextProps.skipFormStateUpdate &&
      JSON.stringify(prevProps.rules) === JSON.stringify(nextProps.rules)
    );
  }
);

/**
 * Hook to create optimized field components
 * Useful for creating multiple similar fields
 */
export function useOptimizedField(defaultProps: Partial<OptimizedFormFieldProps>) {
  return useMemo(() => {
    const OptimizedFieldWithDefaults: React.FC<OptimizedFormFieldProps> = (props) => (
      <OptimizedFormField {...defaultProps} {...props} />
    );

    return memo(OptimizedFieldWithDefaults);
  }, [defaultProps]);
}

/**
 * Pre-configured optimized fields for common use cases
 */
export const FastFormField = memo<OptimizedFormFieldProps>((props) => (
  <OptimizedFormField {...props} skipFormStateUpdate />
));

export const ValidationOnlyField = memo<OptimizedFormFieldProps>((props) => (
  <OptimizedFormField
    {...props}
    skipFormStateUpdate={false}
    render={({ field, error }) =>
      props.render({
        field,
        error,
        isDirty: false,
        isTouched: false,
      })
    }
  />
));
